import {getDateFromISOString, getISOTimeString} from '@/utils/commons';
import {getModal} from '@/utils/CommonModal';
import {pepirisDisplayInitial} from '@/store/slices/perfectStoreSlice';
import {
  isDisplayStepApplicable,
  isFirstPositionTarget,
  isStoreExecutionPepIrisTarget,
  MIN_PEPIRIS_GUIDELINES,
  PEPIRIS_TASK_NAMES
} from '@/utils/constants/perfectStoreConstants';
import {Constants} from '@/utils/constants/commonConstants';
import {
  normalizePerfectStoreStatus,
  normalizePerfectStoreTargetType
} from '@/helpers/perfectStoreUtils';

// Strips UI-only `${field}Valid` flags from a per-language map.
const mapLanguageText = (languageMap = {}, fields) =>
  Object.entries(languageMap).reduce((acc, [code, entry]) => {
    acc[code] = fields.reduce((fieldAcc, field) => {
      fieldAcc[field] = entry?.[field] || '';
      return fieldAcc;
    }, {});
    return acc;
  }, {});

// Transform UI displayContent format to the API's nested banner/racks format.
export const buildDisplayContentPayload = (displayContent = {}) => ({
  banner: {
    imageUrl: displayContent?.bannerImage?.imageUrl || '',
    filename:
      displayContent?.bannerImage?.fileName ||
      displayContent?.bannerImage?.filename ||
      '',
    translations: mapLanguageText(displayContent?.bannerText || {}, [
      'title',
      'description'
    ])
  },
  racks: (displayContent?.rackDisplays || []).map((rack, idx) => ({
    index: idx + 1,
    imageUrl: rack?.image?.imageUrl || '',
    filename: rack?.image?.fileName || rack?.image?.filename || '',
    // API uses `subHeader`; UI uses `subheader`.
    translations: Object.entries(rack?.content || {}).reduce(
      (acc, [code, entry]) => {
        acc[code] = {
          header: entry?.header || '',
          subHeader: entry?.subheader || ''
        };
        return acc;
      },
      {}
    )
  }))
});

const buildPepIrisImagePayload = (image) => ({
  imageUrl: image?.imageUrl || '',
  filename: image?.fileName || image?.filename || ''
});

const buildPepIrisGuidelinePayload = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    translations: mapLanguageText(row?.content || {}, ['text'])
  }));

// UI pepirisDisplay -> API block. Guideline rows are kept when a toggle is
// off so a later draft reload can restore the text.
export const buildPepIrisDisplayPayload = (pepirisDisplay = {}) => ({
  bannerImage: buildPepIrisImagePayload(pepirisDisplay?.bannerImage),
  thumbnailImage: buildPepIrisImagePayload(pepirisDisplay?.thumbnailImage),
  guidelineImage: buildPepIrisImagePayload(pepirisDisplay?.guidelineImage),
  bannerText: mapLanguageText(pepirisDisplay?.bannerText || {}, [
    'title',
    'description'
  ]),
  targetGuidelinesEnabled: !!pepirisDisplay?.targetGuidelinesEnabled,
  targetGuidelines: buildPepIrisGuidelinePayload(
    pepirisDisplay?.targetGuidelines
  ),
  photoGuidelinesEnabled: !!pepirisDisplay?.photoGuidelinesEnabled,
  photoGuidelines: buildPepIrisGuidelinePayload(pepirisDisplay?.photoGuidelines)
});

const parsePepIrisImage = (image) =>
  image?.imageUrl
    ? {
        imageUrl: image.imageUrl,
        imageUri: image.imageUrl,
        fileName: image.filename || image.fileName || ''
      }
    : null;

const parsePepIrisGuidelines = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    id: row?.id || crypto.randomUUID(),
    content: Object.entries(row?.translations || {}).reduce(
      (acc, [code, entry]) => {
        acc[code] = {text: entry?.text || ''};
        return acc;
      },
      {}
    )
  }));

export const parsePepIrisDisplay = (pepirisDisplay) => {
  if (!pepirisDisplay) {
    return {
      ...pepirisDisplayInitial,
      bannerText: {},
      targetGuidelines: [],
      photoGuidelines: []
    };
  }

  const bannerText = Object.entries(pepirisDisplay.bannerText || {}).reduce(
    (acc, [code, entry]) => {
      acc[code] = {
        title: entry?.title || '',
        description: entry?.description || ''
      };
      return acc;
    },
    {}
  );

  return {
    bannerImage: parsePepIrisImage(pepirisDisplay.bannerImage),
    thumbnailImage: parsePepIrisImage(pepirisDisplay.thumbnailImage),
    guidelineImage: parsePepIrisImage(pepirisDisplay.guidelineImage),
    bannerText,
    targetGuidelinesEnabled: !!pepirisDisplay.targetGuidelinesEnabled,
    photoGuidelinesEnabled: !!pepirisDisplay.photoGuidelinesEnabled,
    targetGuidelines: parsePepIrisGuidelines(pepirisDisplay.targetGuidelines),
    photoGuidelines: parsePepIrisGuidelines(pepirisDisplay.photoGuidelines)
  };
};

// Transform API nested banner/racks format back to UI displayContent format.
// Reverses buildDisplayContentPayload for retrieval/edit scenarios.
export const parseDisplayContent = (displayContent = {}) => {
  const banner = displayContent?.banner || {};
  const racks = displayContent?.racks || [];

  const bannerText = Object.entries(banner?.translations || {}).reduce(
    (acc, [code, entry]) => {
      acc[code] = {
        title: entry?.title || '',
        description: entry?.description || ''
      };
      return acc;
    },
    {}
  );

  const rackDisplays = [...racks]
    .sort((a, b) => (a?.index || 0) - (b?.index || 0))
    .map((rack) => ({
      id: crypto.randomUUID(),
      image: rack?.imageUrl
        ? {
            imageUrl: rack.imageUrl,
            imageUri: rack.imageUrl,
            filename: rack.filename || ''
          }
        : null,
      content: Object.entries(rack?.translations || {}).reduce(
        (acc, [code, entry]) => {
          acc[code] = {
            header: entry?.header || '',
            subheader: entry?.subHeader || ''
          };
          return acc;
        },
        {}
      )
    }));

  return {
    bannerImage: banner?.imageUrl
      ? {
          imageUrl: banner.imageUrl,
          imageUri: banner.imageUrl,
          filename: banner.filename || ''
        }
      : null,
    bannerText,
    rackDisplays
  };
};

const getFileNameFromUrl = (url = '') => {
  if (!url) return '';
  try {
    return new URL(url).pathname.split('/').pop() || '';
  } catch {
    return url.split('/').pop() || '';
  }
};

export const normalizePerfectStoreList = (response = {}) => {
  const items =
    response?.data?.items ??
    response?.data?.content ??
    response?.content ??
    response?.data ??
    [];
  return Array.isArray(items) ? items : [];
};

export const mapPerfectStoreApiToFormData = (apiData = {}) => {
  const displayContent = parseDisplayContent(apiData.displayContent || {});
  const storeTargetFileUrl = apiData.storeTargetFileUrl || '';
  const storeTargetFileName =
    apiData.storeTargetFileName ||
    apiData.storeTargetFilename ||
    getFileNameFromUrl(storeTargetFileUrl);

  return {
    targetType: normalizePerfectStoreTargetType(apiData.targetType || ''),
    name: apiData.name || '',
    nameValid: false,
    status: normalizePerfectStoreStatus(apiData.status || Constants.Draft),
    activeStartDate: getDateFromISOString(apiData.activeStartDate) || '',
    activeStartDateValid: false,
    activeEndDate: getDateFromISOString(apiData.activeEndDate) || '',
    activeEndDateValid: false,
    rewardDate: getDateFromISOString(apiData.pointDeliveryDate) || '',
    rewardDateValid: false,
    hasDisplayDate: !apiData.displaySameAsActive,
    displayStartDate: getDateFromISOString(apiData.displayStartDate) || '',
    displayStartDateValid: false,
    displayEndDate: getDateFromISOString(apiData.displayEndDate) || '',
    displayEndDateValid: false,
    awardPointsWhenTargetMet: !!apiData.awardPointsImmediately,
    requiredUserToJoin: !!apiData.requiredUserToJoin,
    targetMediaFile: storeTargetFileUrl ? [storeTargetFileName] : [],
    storeTargetFileUrl,
    displayContent,
    updatedAt: apiData.updatedAt || apiData.lastUpdatedAt || null,
    createdAt: apiData.createdAt || null,
    // PepIris-specific fields
    taskName: apiData.taskName || '',
    targetCoolerPurity:
      apiData.targetCoolerPurity != null
        ? String(apiData.targetCoolerPurity)
        : '',
    targetCoolerPurityValid: false,
    minimumDistinctSku:
      apiData.minimumDistinctSku != null
        ? String(apiData.minimumDistinctSku)
        : '',
    minimumDistinctSkuValid: false,
    submissionDate: getDateFromISOString(apiData.submissionDate) || '',
    submissionDateValid: false,
    hasValidationDate:
      apiData.validationDate != null ? true : !!apiData.hasValidationDate,
    validationDate: getDateFromISOString(apiData.validationDate) || '',
    validationDateValid: false,
    pepirisDisplay: parsePepIrisDisplay(apiData.pepirisDisplay)
  };
};

export const buildPerfectStorePayload = (
  formData = {},
  {status = Constants.Draft, uuid, id} = {}
) => {
  const displaySameAsActive = !formData.hasDisplayDate;
  const awardPointsImmediately = !!formData.awardPointsWhenTargetMet;
  const displayContent = buildDisplayContentPayload(formData.displayContent);

  const isPepIris = isStoreExecutionPepIrisTarget(formData.targetType);

  return {
    ...(id && {id}),
    targetType: formData.targetType,
    name: formData.name,
    status: status || formData.status || Constants.Draft,
    uuid: uuid ?? undefined,
    storeTargetFileUrl: formData.storeTargetFileUrl || '',
    activeStartDate: formData.activeStartDate
      ? getISOTimeString(formData.activeStartDate, false, true)
      : null,
    activeEndDate: formData.activeEndDate
      ? getISOTimeString(formData.activeEndDate, true, false)
      : null,
    pointDeliveryDate:
      !awardPointsImmediately && formData.rewardDate
        ? getISOTimeString(formData.rewardDate, false, false)
        : null,
    displayStartDate:
      !displaySameAsActive && formData.displayStartDate
        ? getISOTimeString(formData.displayStartDate, false, true)
        : null,
    displayEndDate:
      !displaySameAsActive && formData.displayEndDate
        ? getISOTimeString(formData.displayEndDate, true, false)
        : null,
    displaySameAsActive,
    awardPointsImmediately,
    requiredUserToJoin: !!formData.requiredUserToJoin,
    displayContent,
    // PepIris-specific fields
    ...(isPepIris && {
      taskName: formData.taskName || '',
      targetCoolerPurity:
        formData.taskName === PEPIRIS_TASK_NAMES.COOLER_PURITY
          ? Number(formData.targetCoolerPurity) || null
          : null,
      minimumDistinctSku:
        formData.taskName === PEPIRIS_TASK_NAMES.OUT_OF_STOCK_CE ||
        formData.taskName === PEPIRIS_TASK_NAMES.OUT_OF_STOCK_CEDO_DO
          ? Number(formData.minimumDistinctSku) || null
          : null,
      submissionDate: formData.submissionDate
        ? getISOTimeString(formData.submissionDate, false, false)
        : null,
      validationDate:
        formData.hasValidationDate && formData.validationDate
          ? getISOTimeString(formData.validationDate, false, true)
          : null,
      hasValidationDate: !!formData.hasValidationDate,
      pepirisDisplay: buildPepIrisDisplayPayload(formData.pepirisDisplay)
    })
  };
};

export const hasFieldError = (isValid) => isValid === true;

export const isEmptyValue = (value) =>
  !value || (typeof value === 'string' && value.trim() === '');

// Merges a single field update (plus its paired `${field}Valid` flag) into a
// per-language map — shared by Banner Text and Rack Display content editing.
export const updateLanguageFieldMap = (
  existingMap,
  langCode,
  field,
  value,
  hasError
) => ({
  ...existingMap,
  [langCode]: {
    ...existingMap?.[langCode],
    [field]: value,
    [`${field}Valid`]: hasError
  }
});

const isPepIrisSetupFieldsInvalid = (formData) => {
  if (isEmptyValue(formData?.taskName)) return true;

  if (formData.taskName === PEPIRIS_TASK_NAMES.COOLER_PURITY) {
    if (
      isEmptyValue(formData?.targetCoolerPurity) ||
      hasFieldError(formData?.targetCoolerPurityValid)
    ) {
      return true;
    }
  }

  if (
    formData.taskName === PEPIRIS_TASK_NAMES.OUT_OF_STOCK_CE ||
    formData.taskName === PEPIRIS_TASK_NAMES.OUT_OF_STOCK_CEDO_DO
  ) {
    if (
      isEmptyValue(formData?.minimumDistinctSku) ||
      hasFieldError(formData?.minimumDistinctSkuValid)
    ) {
      return true;
    }
  }

  if (
    isEmptyValue(formData?.submissionDate) ||
    hasFieldError(formData?.submissionDateValid)
  ) {
    return true;
  }

  if (formData?.hasValidationDate) {
    if (
      isEmptyValue(formData?.validationDate) ||
      hasFieldError(formData?.validationDateValid)
    ) {
      return true;
    }
  }

  return false;
};

export const isPerfectStoreSetupInvalid = (formData = {}) => {
  if (!formData?.targetType || isEmptyValue(formData?.name)) {
    return true;
  }
  if (hasFieldError(formData?.nameValid)) {
    return true;
  }

  const isPepIris = isStoreExecutionPepIrisTarget(formData?.targetType);
  const isFirstPosition = isFirstPositionTarget(formData?.targetType);

  if (!isFirstPosition && !isPepIris) {
    return true;
  }

  const hasActiveDateError =
    isEmptyValue(formData?.activeStartDate) ||
    hasFieldError(formData?.activeStartDateValid) ||
    isEmptyValue(formData?.activeEndDate) ||
    hasFieldError(formData?.activeEndDateValid);

  if (hasActiveDateError) return true;

  if (!formData?.awardPointsWhenTargetMet) {
    const hasRewardDateError =
      isEmptyValue(formData?.rewardDate) ||
      hasFieldError(formData?.rewardDateValid);
    if (hasRewardDateError) return true;
  }

  if (
    !Array.isArray(formData?.targetMediaFile) ||
    formData.targetMediaFile.length === 0 ||
    !formData.targetMediaFile[0]
  ) {
    return true;
  }

  if (formData?.hasDisplayDate) {
    const hasDisplayDateError =
      isEmptyValue(formData?.displayStartDate) ||
      hasFieldError(formData?.displayStartDateValid) ||
      isEmptyValue(formData?.displayEndDate) ||
      hasFieldError(formData?.displayEndDateValid);
    if (hasDisplayDateError) return true;
  }

  if (isPepIris && isPepIrisSetupFieldsInvalid(formData)) {
    return true;
  }

  return false;
};

// Checks a per-language entry's required fields (each with a paired `${field}Valid` flag)
const isAnyFieldInvalidForLanguage = (entry, fields) =>
  fields.some(
    (field) =>
      isEmptyValue(entry?.[field]) || hasFieldError(entry?.[`${field}Valid`])
  );

const isBannerTextInvalidForLanguage = (bannerText, code) =>
  isAnyFieldInvalidForLanguage(bannerText?.[code], ['title', 'description']);

const isRackContentInvalidForLanguage = (content, code) =>
  isAnyFieldInvalidForLanguage(content?.[code], ['header', 'subheader']);

const isRackDisplayInvalid = (rack, languages) => {
  if (!rack?.image?.imageUrl) return true;
  return languages.some((lang) =>
    isRackContentInvalidForLanguage(rack?.content, lang.code)
  );
};

const isGuidelineRowsInvalid = (rows, languages) => {
  if (!Array.isArray(rows) || rows.length < MIN_PEPIRIS_GUIDELINES) {
    return true;
  }

  return rows.some((row) =>
    languages.some((lang) =>
      isAnyFieldInvalidForLanguage(row?.content?.[lang.code], ['text'])
    )
  );
};

export const isPepIrisDisplayInvalid = (formData = {}, languages = []) => {
  const display = formData?.pepirisDisplay || {};

  if (
    !display?.bannerImage?.imageUrl ||
    !display?.thumbnailImage?.imageUrl ||
    !display?.guidelineImage?.imageUrl
  ) {
    return true;
  }

  const hasBannerTextError = languages.some((lang) =>
    isAnyFieldInvalidForLanguage(display?.bannerText?.[lang.code], [
      'title',
      'description'
    ])
  );
  if (hasBannerTextError) return true;

  if (
    display.targetGuidelinesEnabled &&
    isGuidelineRowsInvalid(display.targetGuidelines, languages)
  ) {
    return true;
  }

  if (
    display.photoGuidelinesEnabled &&
    isGuidelineRowsInvalid(display.photoGuidelines, languages)
  ) {
    return true;
  }

  return false;
};

export const isPerfectStoreDisplayInvalid = (formData = {}, languages = []) => {
  if (!isDisplayStepApplicable(formData?.targetType)) {
    return false;
  }

  const displayContent = formData?.displayContent || {};

  if (!displayContent?.bannerImage?.imageUrl) {
    return true;
  }

  const hasBannerTextError = languages.some((lang) =>
    isBannerTextInvalidForLanguage(displayContent?.bannerText, lang.code)
  );
  if (hasBannerTextError) return true;

  const rackDisplays = displayContent?.rackDisplays || [];
  if (rackDisplays.length < 1) return true;

  return rackDisplays.some((rack) => isRackDisplayInvalid(rack, languages));
};

export const getPerfectStoreCancelModal = ({show, onConfirm, onClose}) =>
  getModal({
    title: 'Are you sure you want to cancel?',
    text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
    showPrimaryButton: true,
    primaryButtonText: 'Yes',
    onClickPrimaryButton: onConfirm,
    showSecondaryButton: true,
    secondaryButtonText: 'No',
    onClickSecondaryButton: onClose,
    showModal: show,
    onCloseModal: onClose,
    hideCloseButton: false
  });
